import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    if (this.#controller.signal.aborted) this.#controller = new AbortController();
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    // Variant changes replace the gallery with DOMParser nodes. Inline scripts
    // in that response do not execute, so bind each new gallery's XR buttons here.
    if (this.querySelector('[data-product-model-json]')) {
      Shopify.loadFeatures([{ name: 'shopify-xr', version: '1.0', onLoad: this.#setupShopifyXR }]);
    }
  }

  #controller = new AbortController();

  #setupShopifyXR = (errors) => {
    if (errors || !this.isConnected) return;
    if (!window.ShopifyXR) {
      document.addEventListener('shopify_xr_initialized', () => this.#setupShopifyXR(), {
        once: true,
        signal: this.#controller.signal,
      });
      return;
    }
    const modelJSON = this.querySelector('[data-product-model-json]');
    if (modelJSON) {
      window.ShopifyXR.addModels(JSON.parse(modelJSON.textContent));
      modelJSON.remove();
    }
    window.ShopifyXR.setupXRElements();
  };

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // Amethysal : galerie commune à toutes les variantes (affiches) : on la garde telle quelle, sans rechargement.
    if (this.hasAttribute('data-keep-on-variant')) return;
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    // Keep the Solstice AR link outside the gallery's stacking context so the
    // full-image previous/next overlay cannot intercept it.
    if (this.nextElementSibling?.matches('.solstice-ar-link')) {
      const newARLink = newMediaGallery.nextElementSibling;
      if (newARLink?.matches('.solstice-ar-link')) this.nextElementSibling.replaceWith(newARLink);
    }
    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
